import { createSlice } from '@reduxjs/toolkit';
import { borrowerProfileAPI } from 'redux/services/borrower-profile';
import { getInvalidationTagsFromFunctionCalls } from 'constants/aiToolInvalidationMap';

/**
 * MIRA's writes to a loan, while the profile sections they touched reload.
 *
 * The chat panel hears that a writing tool finished; the profile page is a
 * separate tree and shows "MIRA is updating…" until the reload lands. Each
 * finished write is its own entry, so a turn that calls application_updates
 * twice keeps the indicator up until the second reload is done — clearing on
 * the first would show the profile as settled with a write still landing.
 */

// A section GET can come back in 150ms; a chip that blinks reads as a glitch.
const MIN_VISIBLE_MS = 800;
// A request that hangs must not leave the indicator spinning for good.
const MAX_WAIT_MS = 15000;
const POLL_MS = 100;
// The composite refetch is the heavy one, held back so the section GETs go first.
const DEFERRED_DELAY_MS = 500;

let lastToken = 0;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeDisplayId = (id) => {
  if (id === null || id === undefined) return null;
  const trimmed = String(id).trim().toUpperCase();
  return trimmed || null;
};

const aiProfileSyncSlice = createSlice({
  name: 'aiProfileSync',
  initialState: {
    // token → the Profile IDs that write named (null when it named none)
    inFlight: {},
  },
  reducers: {
    aiRefreshStarted: (state, action) => {
      const { token, displayIds } = action.payload;
      state.inFlight[token] = displayIds;
    },
    aiRefreshSettled: (state, action) => {
      delete state.inFlight[action.payload];
    },
  },
});

export const { aiRefreshStarted, aiRefreshSettled } = aiProfileSyncSlice.actions;

const isPending = (entry) => entry?.status === 'pending';

const profileApiBusy = (state) => {
  const apiState = state[borrowerProfileAPI.reducerPath];
  return (
    Object.values(apiState?.queries ?? {}).some(isPending) ||
    Object.values(apiState?.mutations ?? {}).some(isPending)
  );
};

/**
 * Reload what the given tool calls wrote, holding the indicator up meanwhile.
 * `calls` is [{ name, displayId }]; calls that write nothing are ignored, and
 * a batch of only those shows nothing at all.
 */
export const refreshProfileAfterAiWrite = (calls) => async (dispatch, getState) => {
  const { immediate, deferred } = getInvalidationTagsFromFunctionCalls(calls);
  if (immediate.length === 0 && deferred.length === 0) return;

  lastToken += 1;
  const token = lastToken;
  const startedAt = Date.now();
  const displayIds = [...new Set(calls.map((call) => normalizeDisplayId(call?.displayId)))];
  dispatch(aiRefreshStarted({ token, displayIds }));

  try {
    if (immediate.length > 0) {
      dispatch(borrowerProfileAPI.util.invalidateTags(immediate));
    }
    if (deferred.length > 0) {
      await sleep(DEFERRED_DELAY_MS);
      dispatch(borrowerProfileAPI.util.invalidateTags(deferred));
    }
    // RTK 2 holds an invalidation while any request on this API is in flight,
    // and starts the refetches the moment it lets go. So "nothing pending"
    // means the sections have reloaded, including when the hold kicked in.
    while (profileApiBusy(getState()) && Date.now() - startedAt < MAX_WAIT_MS) {
      await sleep(POLL_MS);
    }
    const shownFor = Date.now() - startedAt;
    if (shownFor < MIN_VISIBLE_MS) {
      await sleep(MIN_VISIBLE_MS - shownFor);
    }
  } finally {
    dispatch(aiRefreshSettled(token));
  }
};

/**
 * Whether MIRA is mid-write on this loan. A write that named no loan counts
 * for whichever profile is open, since its reload runs there regardless; a
 * write to a different loan does not.
 */
export const selectAiUpdatingLoan = (displayId) => (state) => {
  const open = normalizeDisplayId(displayId);
  return Object.values(state.aiProfileSync?.inFlight ?? {}).some((ids) =>
    ids.some((id) => id === null || open === null || id === open)
  );
};

export default aiProfileSyncSlice.reducer;
